import { Link } from "react-router-dom";
import { useState } from "react";
import { Mail, Instagram } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { toast } from "sonner";
import { useSettings } from "../contexts/SettingsContext";

export default function Footer() {
  const { settings } = useSettings();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("Welcome to the GOSHI atelier.");
      setEmail("");
    } catch (err) {
      toast.error(formatErr(err));
    } finally { setBusy(false); }
  };

  const brand = settings.brand_name || "GOSHI";

  return (
    <footer className="border-t border-[rgba(244,244,240,0.06)] mt-32">
      <div className="px-6 md:px-12 py-20 grid md:grid-cols-4 gap-12">
        <div className="md:col-span-2">
          {settings.logo_url ? (
            <img
              src={settings.logo_url}
              alt={brand}
              className="h-12 w-auto object-contain mb-4"
              style={{ imageRendering: "-webkit-optimize-contrast" }}
            />
          ) : (
            <div className="font-serif text-3xl tracking-[0.35em] mb-4">{brand}</div>
          )}
          <p className="text-muted text-sm max-w-md leading-relaxed">{settings.footer_tagline}</p>
          <form onSubmit={subscribe} className="mt-8 flex gap-3 max-w-md" data-testid="newsletter-form">
            <input
              type="email"
              required
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-line flex-1"
              data-testid="newsletter-input"
            />
            <button className="btn-outline" disabled={busy} data-testid="newsletter-submit">Subscribe</button>
          </form>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gold mb-5">Shop</div>
          <ul className="space-y-3 text-sm text-muted">
            <li><Link to="/shop" className="hover:text-gold">All Collection</Link></li>
            <li><Link to="/shop?category=Dresses" className="hover:text-gold">Dresses</Link></li>
            <li><Link to="/shop?category=Outerwear" className="hover:text-gold">Outerwear</Link></li>
            <li><Link to="/shop?category=Knitwear" className="hover:text-gold">Knitwear</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-gold mb-5">Connect</div>
          <ul className="space-y-3 text-sm text-muted">
            {settings.contact_email && (
              <li>
                <a
                  href={`mailto:${settings.contact_email}`}
                  className="inline-flex items-center gap-2 hover:text-gold transition-colors"
                  data-testid="footer-email"
                >
                  <Mail size={14} strokeWidth={1.5} className="text-gold" />
                  {settings.contact_email}
                </a>
              </li>
            )}
            {settings.instagram_url && (
              <li>
                <a
                  href={settings.instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 hover:text-gold transition-colors"
                  data-testid="footer-instagram"
                  aria-label="Instagram"
                >
                  <Instagram size={14} strokeWidth={1.5} className="text-gold" />
                  Instagram
                </a>
              </li>
            )}
            <li><Link to="/contact" className="hover:text-gold">Contact</Link></li>
            <li><Link to="/about" className="hover:text-gold">About</Link></li>
            <li><Link to="/track-order" className="hover:text-gold" data-testid="footer-track-order">Track Order</Link></li>
          </ul>
        </div>
      </div>
      <div className="px-6 md:px-12 py-6 border-t border-[rgba(244,244,240,0.06)] flex flex-col md:flex-row gap-3 justify-between items-start md:items-center text-xs text-muted uppercase tracking-[0.2em]">
        <span>© {new Date().getFullYear()} {brand} Atelier</span>
        <div className="flex items-center gap-5">
          {settings.instagram_url && (
            <a
              href={settings.instagram_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted hover:text-gold transition-colors"
              aria-label="Instagram"
              data-testid="footer-instagram-icon"
            >
              <Instagram size={16} strokeWidth={1.5} />
            </a>
          )}
          <span>Crafted with intention</span>
        </div>
      </div>
    </footer>
  );
}
